<script setup>
import CommonUtil from '@/components/CommonUtil';
import { useAxios } from '@/hooks/useAxios';
import AppPopup from '@/layout/AppPopup.vue';
import { useCommonCodeStore } from '@/store/commoncode-store';
import ScrollPanel from 'primevue/scrollpanel';
import { computed, nextTick, onMounted, ref, watch } from 'vue';

const props = defineProps({
    modelValue: {
        type: [String, Number],
        default: ''
    },
    inputId: {
        type: String,
        default: ''
    },
    label: {
        type: String,
        default: ''
    },
    required: {
        type: Boolean,
        default: false
    },
    placeholder: {
        type: String,
        default: ''
    },
    disabled: {
        type: Boolean,
        default: false
    },
    marginTop: {
        type: String,
        default: '0'
    },
    marginBottom: {
        type: String,
        default: '0'
    },
    marginLeft: {
        type: String,
        default: '0'
    },
    marginRight: {
        type: String,
        default: '0'
    },
    labelRightmargin: {
        type: String,
        default: '2rem'
    },
    visible: {
        type: Boolean,
        default: true
    },
    button: {
        type: String,
        default: ''
    },
    upper: {
        type: Boolean,
        default: false
    },
    validationCheck: {
        type: Function,
        default: () => ({ isValid: true, message: '' })
    },
    readonly: {
        type: Boolean,
        default: false
    },
    labelEmpty: {
        type: Boolean,
        default: false
    },
    dataList: {
        type: Array,
        default: () => []
    },
    headers: {
        type: Array,
        default: () => []
    }
});

const emit = defineEmits(['update:modelValue', 'input', 'click', 'change']);
const dataList = computed(() => props.dataList);
const headers = computed(() => props.headers);
const ValidationMsg = ref('');
const isPopupVisible = ref(false);
const isVisible = computed(() => props.visible);

const toastComponentRef = ref(null);
const showConfirmDialog = ref(false);
const confirmAction = ref(null);
const dialogHeader = ref('');
const dialogMessage = ref('');
const dialogcomplete = ref('');

const Utils = new CommonUtil();
const gridRef = ref(null); // 그리드api
const appStepperRef2 = ref(null);

const showPopup = ref(false);
const urlPost = ref('');
const apiType = ref('');
const loading = ref(false);

const commonCodes = computed(() => useCommonCodeStore().getCommonCodes);

const commonCode = (code) => {
    const returnVal = computed(() => {
        return commonCodes.value[code];
    });
    return returnVal;
};

onMounted(() => {
    search();
});

watch(isPopupVisible, (newValue) => {
    if (newValue) {
        searchQuery.value = '';
        filterOptions();
    }
});

const rowData = ref([]);

let episode = ref([]);
let episodeComputed = computed(() => episode);

watch(
    () => props.dataList,
    async (newVal) => {
        rowData.value = Array.isArray(newVal) ? newVal : [];
        if (newVal.length > 0 && newVal[0].payment_status === 'CANCEL') {
            loading.value = true;
        } else {
            loading.value = false;
        }

        episode = Array.from({ length: newVal.length }, (_, i) => ({
            id: i + 1,
            text: `${i + 1}회차`,
            value: i + 1
        }));
    },
    { immediate: true }
);
const setDialogInfo = (action) => {
    confirmAction.value = action;
    dialogHeader.value = dialogInfo[action].header;
    dialogMessage.value = dialogInfo[action].message;
    dialogcomplete.value = dialogInfo[action].completemessage;
};

const dialogInfo = {
    regist: {
        header: '저장',
        message: '헤지 하시겠습니까?',
        completemessage: '중도해지가 완료 되었습니다.'
    },
    modify: {
        header: '수정',
        message: '해지취소 하시겠습니까?',
        completemessage: '저장되었습니다.'
    }
};

const handleConfirm = () => {
    if (isConfirming) return;
    isConfirming = true;

    if (toastComponentRef.value) {
        toastComponentRef.value.showToast('success', dialogHeader.value, dialogcomplete.value);
    }

    showConfirmDialog.value = false;
    setTimeout(() => {
        isConfirming = false;
    }, 1000);
};

const handleCancel = () => {
    if (isConfirming) return;
    showConfirmDialog.value = false;
};

const handlePayment = (data) => {
    stepperPanels.value = stepperPanelsConfig['panel3'];
    showPopup.value = true;

    nextTick(() => {
        appStepperRef2.value.setReadonly({ schedule_sequence: true });
        appStepperRef2.value.updateInputval('schedule_sequence', data['schedule_sequence'].toString());
        appStepperRef2.value.updateInputval('actual_amount', data['scheduled_amount'].toString());
    });
};

const isFutureDate = (val) => {
    if (!val) return false;
    let dateStr = val.toString().replaceAll('-', '');
    const year = parseInt(dateStr.substring(0, 4), 10);
    const month = parseInt(dateStr.substring(4, 6), 10) - 1;
    const day = parseInt(dateStr.substring(6, 8), 10);
    const inputDate = new Date(year, month, day);
    const today = new Date();
    today.setHours(0, 0, 0, 0);
    return inputDate > today;
};

const columnDefs = ref([
    {
        headerName: '',
        field: 'boolean',
        filter: false,
        minWidth: 10,
        maxWidth: 40,
        cellEditor: 'agCheckboxCellEditor',
        headerCheckboxSelection: true,
        checkboxSelection: true,
        suppressMovable: true,
        cellClass: ['alignC']
    },
    {
        headerName: '회차',
        field: 'schedule_sequence',
        editable: false,
        sortable: false,
        suppressSorting: true,
        suppressMenu: true,
        flex: 1,
        minWidth: 30
    },
    {
        field: 'schedule_date',
        headerName: '납부 예정일자',
        cellStyle: { textAlign: 'center' },
        cellEditor: Utils.createDateInputComponent(),
        singleClickEdit: true,
        editable: true,
        editable: (params) => {
            let status = params.data?.payment_status;
            return status === 'PAID' ? false : true;
        },
        cellClassRules: {
            'future-date': (params) => isFutureDate(params.value) // 오늘 이후면 클래스 적용
        },
        width: 130,
        valueFormatter: (param) => Utils.dateToSeoul(param.value)
    },
    // {
    //     headerName: '납부예정금액',
    //     field: 'scheduled_amount',
    //     sortable: true,
    //     flex: 1,
    //     width: 130,
    //     maxWidth: 130,
    //     editable: false,
    //     headerClass: '',
    //     singleClickEdit: true,
    //     hide: false,
    //     cellClass: ['alignR'],
    //     cellClassRules: {
    //         'future-date': (params) => isFutureDate(params.data['schedule_date']) // 오늘 이후면 클래스 적용
    //     },
    //     valueFormatter: (params) => {
    //         if (params.value === null || params.value === undefined) return '';
    //         const formattedValue = Math.floor(params.value)
    //             .toString()
    //             .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
    //         return `${formattedValue}원`;
    //     }
    // },

    {
        headerName: '납부 예정금액',
        field: 'scheduled_amount',
        sortable: false,
        flex: 1,
        maxWidth: 130,
        editable: true,
        headerClass: '',
        singleClickEdit: true,
        hide: false,
        editable: (params) => {
            let status = params.data?.payment_status;
            return status === 'PAID' ? false : true;
        },
        cellClassRules: {
            'future-date': (params) => isFutureDate(params.data['schedule_date']) // 오늘 이후면 클래스 적용
        },
        valueFormatter: (params) => {
            if (params.value === null || params.value === undefined) return '';
            const formattedValue = Math.floor(params.value)
                .toString()
                .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
            return `${formattedValue}원`;
        }
    },
    {
        field: 'payment_date',
        headerName: '실제 납부일자',
        cellEditor: Utils.createDateInputComponent(),
        singleClickEdit: true,
        editable: false,
        width: 130,
        cellClassRules: {
            'future-date': (params) => isFutureDate(params.data['schedule_date'])
        },
        valueFormatter: (param) => Utils.dateToSeoul(param.value)
    },
    {
        headerName: '실제 납부금액',
        field: 'actual_amount',
        sortable: false,
        flex: 1,
        maxWidth: 130,
        editable: false,
        headerClass: '',
        singleClickEdit: true,
        hide: false,
        cellClassRules: {
            'future-date': (params) => isFutureDate(params.data['schedule_date'])
        },
        valueFormatter: (params) => {
            if (params.value === null || params.value === undefined) return '';
            const formattedValue = Math.floor(params.value)
                .toString()
                .replace(/(\d)(?=(\d{3})+(?!\d))/g, '$1,');
            return `${formattedValue}원`;
        }
    },
    {
        headerName: '특이사항',
        field: 'remark',
        editable: false,
        sortable: false,
        suppressSorting: true,
        suppressMenu: true,
        flex: 1,
        minWidth: 100,
        maxWidth: 550,
        cellClassRules: {
            'future-date': (params) => isFutureDate(params.data['schedule_date'])
        }
    },
    {
        headerName: '입금하기',
        field: 'setting',
        editable: false,
        flex: 1,
        minWidth: 110,
        maxWidth: 110,
        cellClass: ['alignC'],
        cellRenderer: (params) => {
            let status = params.data?.payment_status;
            if (status === 'PAID') {
                return `<span>납부완료</span>`;
            }
            if (isFutureDate(params.data?.schedule_date)) {
                return;
            }
            const container = document.createElement('div');
            const btn = document.createElement('button');
            btn.className = 'pi pi-wallet';
            btn.style.cursor = 'pointer';
            btn.style.fontSize = '1rem';
            btn.style.color = '#3869FF';
            btn.addEventListener('click', () => {
                handlePayment(params.data);
            });
            container.appendChild(btn);
            return container;
        }
    }
]);
const onGridReady = (params, gridName) => {
    gridApi.value = params.api;
};

const onSelectionChanged = (data) => {
    if (!data || !data.selectedRows) {
        return;
    }
    data.selectedRows.forEach((row) => {
        const newRow = row.row.isNew === true ? true : row.newRow;
    });
};

const onCellClicked = (params) => {
    if (params.colDef.checkboxSelection) {
        const selected = !params.node.isSelected();
        params.node.setSelected(selected);
        params.node.setDataValue(params.colDef.field, selected);
    }
};

const onSave = () => {
    const selectedRows = gridRef.value.getSelectedRows();
    let selectData = selectedRows;
    if (selectData && !selectData === false && selectData.length < 1) {
        if (toastComponentRef.value) {
            toastComponentRef.value.showToast('success', '납부 수정', '수정할 항목을 선택해주세요.');
        }
        return;
    }

    const data = JSON.parse(JSON.stringify(selectData[0]));
    let config = { method: 'put' };

    try {
        const { execute } = useAxios('/api/payment-schedule/info', config, {
            immediate: false,
            onSuccess(response) {
                if (toastComponentRef.value) {
                    toastComponentRef.value.showToast('success', '수정', '납부수정이 완료되었습니다.');
                }
                search(); // 그리드 재 랜더링용
            },
            onError(err) {
                if (toastComponentRef.value) {
                    toastComponentRef.value.showToast('error', '납부수정', err.errorMessage);
                }
            }
        });

        execute(data);
    } catch (error) {
        console.error('', error);
    } finally {
    }
};

const onCancel = () => {
    stepperPanels.value = stepperPanelsConfig['panel1'];
    showPopup.value = true;
};
const onRplaceCancel = () => {
    stepperPanels.value = stepperPanelsConfig['panel2'];
    showPopup.value = true;
};

const execute = (data) => {
    const method = apiType.value || 'post';

    return new Promise((resolve, reject) => {
        let callApi = useAxios(
            urlPost.value,
            { method },
            {
                immediate: false,
                onSuccess(response) {
                    resolve({
                        success: true,
                        message: 'API 호출에 성공.',
                        details: response // 에러 객체 또는 추가 데이터
                    });
                },
                onError(response) {
                    reject({
                        success: false,
                        message: 'API 호출에 실패했습니다.',
                        details: response // 에러 객체 또는 추가 데이터
                    });
                }
            }
        ).execute(data);
        return callApi;
    });
};

let stepperPanelsConfig = {
    panel1: [
        {
            title: '중도해지',
            content: [
                {
                    marginTop: '1rem',
                    components: [
                        {
                            type: 'combobox',
                            width: '62%',
                            marginLeft: '0%',
                            marginRight: '0%',
                            props: {
                                inputId: 'schedule_sequence',
                                options: episodeComputed,
                                placeholder: '납부회차 선택',
                                required: true,
                                label: '납부회차'
                            }
                        }
                    ]
                },
                {
                    marginTop: '1rem',
                    components: [
                        {
                            type: 'textarea',
                            width: '100%',
                            marginLeft: '0%',
                            marginRight: '0%',
                            props: {
                                inputId: 'reservation_no',
                                label: '해지사유',
                                placeholder: '해지사유 입력 (계약서 메모에 등록됩니다)',
                                required: false,
                                height: '8rem'
                            }
                        }
                    ]
                }
            ],
            buttons: [
                {
                    label: '해지하기',
                    onClick: async (data, action) => {
                        urlPost.value = '/api/payment-schedule/cancel-early';
                        apiType.value = 'PUT';
                        let reservationNo = dataList.value.length > 0 ? dataList.value[0]['reservation_no'] : '';
                        let newData = data;
                        newData['reservation_no'] = reservationNo;
                        execute(data)
                            .then((isSuccess) => {
                                if (isSuccess.success) {
                                    loading.value = true;
                                    showPopup.value = false;
                                    if (toastComponentRef.value) {
                                        toastComponentRef.value.showToast('success', '저장', '정상적으로 해지완료 되었습니다.');
                                    }
                                    //완료 팝업 뜰 수 있도록 한다.
                                }
                            })
                            .catch((isFailure) => {
                                if (!isFailure.success) {
                                    if (toastComponentRef.value) {
                                        toastComponentRef.value.showToast('error', '중도해지', '중도해지에 실패하였습니다.');
                                    }
                                }
                            });
                    }
                }
            ]
        }
    ],
    panel2: [
        {
            title: '중도해지취소',
            content: [
                {
                    marginTop: '1rem',
                    components: [
                        {
                            type: 'textarea',
                            width: '100%',
                            marginLeft: '0%',
                            marginRight: '0%',
                            props: {
                                inputId: 'reservation_no',
                                label: '해지 취소 사유',
                                placeholder: '해지 취소 사유 입력 (계약서 메모에 등록됩니다)',
                                required: false,
                                height: '8rem'
                            }
                        }
                    ]
                }
            ],
            buttons: [
                {
                    label: '해지취소',
                    onClick: async (data, action) => {
                        let reservationNo = dataList.value.length > 0 ? dataList.value[0]['reservation_no'] : '';
                        urlPost.value = `/api/payment-schedule/cancel-rollback/${reservationNo}`;
                        apiType.value = 'PUT';
                        execute(data)
                            .then((isSuccess) => {
                                if (isSuccess.success) {
                                    loading.value = false;
                                    showPopup.value = false;
                                    if (toastComponentRef.value) {
                                        toastComponentRef.value.showToast('success', '해지취소', '해지취소 처리 완료되었습니다.');
                                    }
                                }
                            })
                            .catch((isFailure) => {
                                if (toastComponentRef.value) {
                                    toastComponentRef.value.showToast('error', '해지취소', '해지취소가 정상적으로 완료되지 못했습니다.');
                                }
                            });
                    }
                }
            ]
        }
    ],
    panel3: [
        {
            title: '납부회차',
            content: [
                {
                    marginTop: '1rem',
                    components: [
                        {
                            type: 'combobox',
                            width: '50%',
                            marginLeft: '0%',
                            marginRight: '0.4rem',
                            props: {
                                inputId: 'schedule_sequence',
                                options: episodeComputed,
                                label: '납부회차',
                                required: true,
                                selectAll: '전체',
                                read: true,
                                readonly: true
                            }
                        },
                        {
                            type: 'datepicker',
                            width: '50%',
                            marginLeft: '0%',
                            marginRight: '0%',
                            props: {
                                inputId: 'payment_date',
                                label: '납부일자',
                                required: true,
                                selectAll: '전체'
                            }
                        }
                    ]
                },
                {
                    marginTop: '1rem',
                    components: [
                        {
                            type: 'inputbox',
                            width: '50%',
                            marginLeft: '0%',
                            marginRight: '0.4rem',
                            props: {
                                inputId: 'actual_amount',
                                label: '납부금액',
                                required: true,
                                placeholder: '납부금액 입력',
                                unit: '원'
                            }
                        },
                        {
                            type: 'combobox',
                            width: '50%',
                            marginLeft: '0%',
                            marginRight: '0%',
                            colspan: 3,
                            props: {
                                inputId: 'payment_method',
                                label: '납부방법',
                                options: commonCode('PAYMENT_METHOD'),
                                placeholder: '납부방법 입력',
                                required: false
                            }
                        }
                    ]
                },
                {
                    marginTop: '1rem',
                    components: [
                        {
                            type: 'textarea',
                            width: '100%',
                            marginLeft: '0%',
                            marginRight: '0%',
                            colspan: 6,
                            props: {
                                inputId: 'remark',
                                label: '메모',
                                placeholder: '납부회차 메모입력',
                                required: false,
                                height: '5rem'
                            }
                        }
                    ]
                }
            ],
            buttons: [
                {
                    label: '입금하기',
                    onClick: async (data, action) => {
                        let reservationNo = dataList.value.length > 0 ? dataList.value[0]['reservation_no'] : '';
                        let newData = data;
                        newData['reservation_no'] = reservationNo;
                        urlPost.value = `/api/payment-schedule`;
                        apiType.value = 'PUT';
                        execute(newData)
                            .then((isSuccess) => {
                                if (isSuccess.success) {
                                    loading.value = false;
                                    showPopup.value = false;
                                    if (toastComponentRef.value) {
                                        toastComponentRef.value.showToast('success', '입금하기', '입금처리 완료되었습니다.');
                                    }
                                    search(); // 그리드 재 랜더링용
                                }
                            })
                            .catch((isFailure) => {
                                if (toastComponentRef.value) {
                                    toastComponentRef.value.showToast('error', '입금하기', isFailure.details.errorMessage);
                                }
                            });
                    }
                }
            ]
        }
    ]
};
const stepperPanels = ref(stepperPanelsConfig['panel3']);
// stepperPanelsConfig['panel3']
const search = async () => {
    rowData.value = [];
    let reservationNo = dataList.value.length > 0 ? dataList.value[0]['reservation_no'] : '';
    const url = `/api/payment-schedule/${reservationNo}`;
    const config = {
        method: 'get'
    };
    const { data, error, execute } = useAxios(url, config, {
        onSuccess(response) {
            if (toastComponentRef.value) {
                toastComponentRef.value.showToast('success', '조회', '조회 되었습니다.');
            }
            rowData.value = response.data.data;
        },
        onError(err) {
            if (toastComponentRef.value) {
                toastComponentRef.value.showToast('error', '조회 실패', err.message);
            }
            console.error('Error:', err.message);
        }
    });
};
</script>

<template>
    <div
        v-if="isVisible"
        class="flex flex-col"
        :style="{
            marginTop: marginTop,
            marginBottom: marginBottom,
            marginLeft: marginLeft,
            marginRight: marginRight
        }"
    >
        <AppPopup v-if="showPopup" ref="appStepperRef2" v-model:isPopupVisible="showPopup" :panels="stepperPanels" :action="'modify'" :contentHeight="'36rem'" :contentWidth="'36rem'" />
        <ToastComponent ref="toastComponentRef" />
        <ConfirmComponent
            v-model:visible="showConfirmDialog"
            :dialogHeader="dialogHeader"
            :dialogMessage="dialogMessage"
            dialogIcon="pi pi-exclamation-triangle"
            dialogWidth="400px"
            dialogButtonSeverity="danger"
            @confirm="handleConfirm"
            @cancel="handleCancel"
        />
        <div>
            <div class="buttons-container flex-row-reverse" :class="{ 'empty-container': props.empty }">
                <button @click="onSave">저장</button>
                <br />
                <div v-if="!loading">
                    <button @click="onCancel">중도해지</button>
                </div>
                <div v-else>
                    <button @click="onRplaceCancel">해지취소</button>
                </div>
            </div>
            <ScrollPanel style="width: 100%; height: 390px">
                <GridComponent
                    :key="rowData.length"
                    ref="gridRef"
                    :columnDefs="columnDefs"
                    :rowData="rowData"
                    :gridName="'mainGrid'"
                    :paginationPageSize="20"
                    rowSelection="single"
                    :showClearButton="false"
                    :showSaveButton="false"
                    :showDeleteButton="false"
                    :showAddButton="false"
                    :loading="loading"
                    :overlayLoadingTemplate="'<span class=\'custom-loading\'><p>중도해지 상태입니다</p><p>보증금 및 선수금, 대여료는 입금내역 에서 환불처리 가능합니다<p/> </span>'"
                    @gridReady="(params) => onGridReady"
                    @selectionChanged="onSelectionChanged"
                    @cellClicked="onCellClicked"
                />
            </ScrollPanel>
        </div>
    </div>
</template>

<style scoped>
.input-wrapper {
    display: flex;
    flex-direction: column;
}

.label {
    font-family: 'SCoreDream600';
    display: flex;
    align-items: center;
    margin-bottom: 0.25rem;
    margin-left: 0.25rem;
    height: 1.3rem;
}

.required-asterisk {
    color: #f87171;
    margin-left: 0.2rem;
    position: relative;
    top: -0.2rem;
}

.input-font {
    font-family: 'SCoreDream400';
    font-size: 0.9rem;
}

input:focus {
    border-color: #0038ea;
    outline: none;
}

.text-right {
    text-align: right;
    padding-right: 6rem;
}

.error-border {
    border: 1px solid red !important;
}
.custom-input.p-inputtext {
    padding: 0.75rem;
    border: 1px solid #d1d5db;
    border-radius: 0.5rem;
}

.custom-input.p-inputtext:focus {
    border-color: #0038ea !important;
}

.button-style_b {
    font-family: 'SCoreDream500';
    font-size: 0.85rem;
    transition: color 0.3s ease;
    border-radius: 0.5rem;
    background-color: #f1f1f1;
    color: #4b5563;
    align-items: center;
    position: absolute;
    right: 0.5rem;
    width: 5.5rem;
    height: 1.8rem;
}
.button-style {
    font-family: 'SCoreDream500';
    font-size: 1rem;
    transition: color 0.3s ease;
    border-radius: 0.5rem;
    background-color: #f1f1f1;
    color: #4b5563;
    align-items: center;
    position: absolute;
    height: 2.8rem;
    width: 10rem;
    display: flex;
    justify-content: center;
}

.button-style:hover {
    background-color: #dddee0;
}

.button-style:focus {
    outline: none;
}

.validation-message {
    color: #f87171;
    font-size: 0.75rem;
    margin-left: 0.5rem;
}

.unit-text {
    font-family: 'SCoreDream500';
    font-size: 0.8rem;
    color: #4b5563;
    height: 100%;
    display: flex;
    align-items: center;
    position: absolute;
    padding-top: 0.2rem;
    right: 2.1rem;
    width: 2rem;
}

.clear-button {
    cursor: pointer;
    background: transparent;
    border: none;
    font-size: 0.9rem;
    color: #6b7280;
    position: absolute;
    right: 0.1rem;
}

.clear-button:hover {
    color: #4b5563;
}

.popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.popup-content {
    background: #fff;
    padding: 1.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    text-align: left;
    min-width: 30rem;
}

.popup-item {
    margin: 0.5rem 0;
    font-family: 'SCoreDream400';
    font-size: 0.95rem;
    cursor: pointer;
}

.popup-item:hover {
    color: #0038ea;
}

.close-button {
    font-family: 'SCoreDream500';
    font-size: 0.9rem;
    margin-top: 1rem;
    background: #f1f1f1;
    padding: 0.5rem 1rem;
    border: none;
    border-radius: 0.25rem;
    cursor: pointer;
}

.close-button:hover {
    background: #ddd;
}

.popup-overlay {
    position: fixed;
    top: 0;
    left: 0;
    width: 100%;
    height: 100%;
    background: rgba(0, 0, 0, 0.5);
    display: flex;
    justify-content: center;
    align-items: center;
    z-index: 9999;
}

.popup-content {
    background: #fff;
    padding: 1.5rem;
    border-radius: 0.5rem;
    box-shadow: 0 2px 10px rgba(0, 0, 0, 0.1);
    width: auto;
    max-height: 80vh;
    display: flex;
    flex-direction: column;
}

.search-input {
    width: 100%;
    padding: 0.5rem;
    margin-bottom: 1rem;
    border: 1px solid #ccc;
    border-radius: 0.25rem;
}

.options-container {
    flex-grow: 1;
    height: 18rem;
    max-height: 30rem;
    overflow-y: auto;
}

.popup-item {
    padding: 0.4rem;
    cursor: pointer;
    transition: background-color 0.2s;
}

.popup-item:hover {
    background-color: #f0f0f0;
}

.options-container::-webkit-scrollbar {
    width: 0.5rem;
}

.options-container::-webkit-scrollbar-track {
    background: #f1f1f1;
    border-radius: 8px;
}

.options-container::-webkit-scrollbar-thumb {
    background: #c9c6c6;
    border-radius: 8px;
}

.options-container::-webkit-scrollbar-thumb:hover {
    background: #727272;
}

.note-grid {
    width: 100%;
    border-collapse: collapse;
    margin-top: 1rem;
}

.note-grid th,
.note-grid td {
    border: 0.1rem solid #ddd;
    border-left: none;
    border-right: none;
    padding: 0.3rem;
    font-family: 'SCoreDream400';
    font-size: 0.875rem;
    text-align: center;
    max-width: 15rem;
}

.note-grid th {
    background-color: #f2f2f2;
}

.note-grid tr:nth-child(even) {
    background-color: #f9f9f9;
}

.note-grid th:hover {
    cursor: default;
}

.note-grid tr:hover {
    background-color: #f5f5f5;
    cursor: pointer;
}
</style>
